import { useState, useEffect } from 'react';
import { has, isArray } from 'lodash';
import api from '../services/api';
import { socketConnection } from '../services/socket';
import { toast } from '@7shifts/sous-chef';
import { AxiosError } from 'axios';
import { __ } from 'i18n';
import {
    formatEffectiveDueDate,
    isMembershipExpired,
    persistCompanyDueDate
} from '../domain/membership';
import { Setting, User } from 'types';

const useAuth = () => {
    const [isAuth, setIsAuth] = useState(false);
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User>();
    const [settings, setSettings] = useState<Setting[]>();

    useEffect(() => {
        const requestInterceptor = api.interceptors.request.use(
            (config) => {
                const token = localStorage.getItem('token');
                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = api.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;
                if (
                    error?.response?.status === 403 &&
                    !originalRequest._retry
                ) {
                    originalRequest._retry = true;

                    const refreshTokenRequest =
                        localStorage.getItem('refreshToken');
                    if (refreshTokenRequest) {
                        api.defaults.headers['x-auth-token'] =
                            refreshTokenRequest;
                    }

                    const { data } = await api.post('/auth/refresh_token');
                    if (data) {
                        localStorage.setItem('token', data.token);
                        localStorage.setItem('refreshToken', data.refreshToken);
                        api.defaults.headers['x-auth-token'] = data.refreshToken;
                        api.defaults.headers.Authorization = `Bearer ${data.token}`;

                        // Vencimento pode ter passado durante a sessão: reavalia
                        // com o dado fresco e bloqueia sem repetir a requisição.
                        const rawDueDate = data.user?.company?.dueDate;
                        if (rawDueDate) {
                            persistCompanyDueDate(rawDueDate);
                            if (isMembershipExpired(rawDueDate)) {
                                toast(
                                    __('loginPage.membershipExpired', {
                                        expireDate: formatEffectiveDueDate(
                                            rawDueDate
                                        )
                                    }),
                                    'danger'
                                );
                                localStorage.removeItem('token');
                                localStorage.removeItem('refreshToken');
                                localStorage.removeItem('companyId');
                                api.defaults.headers.Authorization = '';
                                setIsAuth(false);
                                return Promise.reject(error);
                            }
                        }
                    }
                    return api(originalRequest);
                }
                if (error?.response?.status === 401) {
                    localStorage.removeItem('refreshToken');
                    localStorage.removeItem('token');
                    localStorage.removeItem('companyId');
                    api.defaults.headers.Authorization = '';
                    setIsAuth(false);
                }
                return Promise.reject(error);
            }
        );

        return () => {
            api.interceptors.request.eject(requestInterceptor);
            api.interceptors.response.eject(responseInterceptor);
        };
    }, []);

    useEffect(() => {
        const refreshTokenRequest = localStorage.getItem('refreshToken');
        (async () => {
            if (refreshTokenRequest) {
                try {
                    const { data } = await api.post(
                        '/auth/refresh_token',
                        {},
                        {
                            headers: {
                                'x-auth-token': refreshTokenRequest
                            }
                        }
                    );

                    // Reavalia o vencimento com o dado fresco vindo do refresh,
                    // mantendo o cache atualizado em sessões longas.
                    const rawDueDate = data.user?.company?.dueDate;
                    if (rawDueDate) {
                        persistCompanyDueDate(rawDueDate);
                        if (isMembershipExpired(rawDueDate)) {
                            toast(
                                __('loginPage.membershipExpired', {
                                    expireDate: formatEffectiveDueDate(
                                        rawDueDate
                                    )
                                }),
                                'danger'
                            );
                            localStorage.removeItem('token');
                            localStorage.removeItem('refreshToken');
                            localStorage.removeItem('companyId');
                            api.defaults.headers.Authorization = '';
                            setIsAuth(false);
                            setLoading(false);
                            return;
                        }
                    }

                    localStorage.setItem('refreshToken', data.refreshToken);
                    localStorage.setItem('token', data.token);
                    api.defaults.headers['x-auth-token'] = data.refreshToken;
                    api.defaults.headers.Authorization = `Bearer ${data.token}`;
                    setIsAuth(true);
                    setUser(data.user);
                    setSettings(data.user.settings);
                } catch (err: unknown) {
                    const message = (err as AxiosError<{ error: string }>)
                        .response?.data.error;

                    toast(
                        __(`apiErrors.${message}` || 'default.genericError'),
                        'danger'
                    );
                }
            }
            setLoading(false);
        })();
    }, []);

    useEffect(() => {
        const companyId = localStorage.getItem('companyId');
        const token = localStorage.getItem('token') ?? '';
        if (!token) {
            return;
        }
        const socket = socketConnection(token);

        socket.on('connect', () => {
            socket.emit('join', 'user');
            socket.emit('join', 'auth');
        });

        socket.on(`user-${companyId}`, (data) => {
            console.log(`LOG: user-${companyId}`, data);
            if (data.action === 'update' && user && data.user.id === user.id) {
                setUser(data.user);
            }
        });

        socket.on(`auth-${companyId}`, (data) => {
            console.log(`LOG: auth-${companyId}`, data);
            if (user && data.user.id === +user.id) {
                toast('Sua conta foi acessada em outro computador.', 'danger');
                setTimeout(() => {
                    localStorage.clear();
                    window.location.reload();
                }, 2000);
            }
        });

        return () => {
            socket.disconnect();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const handleLogin = async (userData: {
        email: string;
        password: string;
    }): Promise<boolean> => {
        setLoading(true);

        try {
            const { data } = await api.post('/auth/login', userData);
            const {
                user: { companyId, id, company }
            } = data;

            if (has(company, 'settings') && isArray(company.settings)) {
                const setting = company.settings.find(
                    (s: { key: string }) => s.key === 'campaignsEnabled'
                );
                if (setting && setting.value === 'true') {
                    localStorage.removeItem('cshow'); //regra pra exibir campanhas
                }
            }

            const rawDueDate = data.user.company.dueDate;

            if (isMembershipExpired(rawDueDate)) {
                toast(
                    __('loginPage.membershipExpired', {
                        expireDate: formatEffectiveDueDate(rawDueDate)
                    }),
                    'danger'
                );
                setLoading(false);
                return false;
            }

            if (!localStorage.getItem('userSoundEnabled')) {
                localStorage.setItem('userSoundEnabled', 'true');
            }

            if (!localStorage.getItem('userPopupEnabled')) {
                localStorage.setItem('userPopupEnabled', 'true');
            }
            localStorage.setItem('token', data.token);
            localStorage.setItem('refreshToken', data.refreshToken);
            localStorage.setItem('companyId', companyId);
            localStorage.setItem('userId', id);
            persistCompanyDueDate(rawDueDate);
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
            api.defaults.headers['x-auth-token'] = data.refreshToken;
            setUser(data.user);
            setSettings(data.user.settings);
            setIsAuth(true);
            setLoading(false);
            return true;
        } catch (err: unknown) {
            const message = (err as AxiosError<{ error: string }>).response
                ?.data.error;

            toast(
                __(`apiErrors.${message}` || 'default.genericError'),
                'danger'
            );
            setLoading(false);
            return false;
        }
    };

    const handleLogout = async (): Promise<boolean> => {
        setLoading(true);

        try {
            await api.delete('/auth/logout');
            setIsAuth(false);
            setUser(undefined);
            localStorage.removeItem('token');
            localStorage.removeItem('refreshToken');
            localStorage.removeItem('companyId');
            localStorage.removeItem('userId');
            localStorage.removeItem('cshow');
            localStorage.removeItem('ticketsPageFilters');
            localStorage.removeItem('impersonatorToken');
            localStorage.removeItem('impersonatorRefreshToken');
            api.defaults.headers.Authorization = '';
            setLoading(false);
            return true;
        } catch (err) {
            toast(err as string, 'danger');
            setLoading(false);
            return false;
        }
    };

    return {
        isAuth,
        user,
        loading,
        settings,
        handleLogin,
        handleLogout
    };
};

export default useAuth;
